import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  FiBriefcase,
  FiChevronDown,
  FiClock,
  FiGift,
  FiGrid,
  FiLayers,
  FiMapPin,
  FiUsers,
  FiDollarSign,
  FiTag,
  FiTarget,
  FiUserCheck,
  FiTruck,
  FiPieChart,
  FiActivity,
  FiUser,
  FiFolder,
  FiCalendar,
  FiCheckCircle,
  FiEdit3,
  FiFileText,
  FiShield,
  FiSettings,
  FiCreditCard,
  FiClipboard,
  FiTool,
  FiFlag,
  FiBook,
  FiX,
} from 'react-icons/fi';
import { hasPermission as checkPermission } from '../../utils/permissions';
import './style.css';

export default function Sidebar({ isOpen, onClose }) {
  // Use ref for onClose to avoid effect re-runs when function identity changes
  const onCloseRef = React.useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Handle body scroll lock when sidebar is open on mobile
  useEffect(() => {
    const handleBodyScroll = () => {
      const isMobile = window.innerWidth < 960;
      if (isOpen && isMobile) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    };

    handleBodyScroll();
    window.addEventListener('resize', handleBodyScroll);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('resize', handleBodyScroll);
    };
  }, [isOpen]);

  // Handle back button on mobile using hash
  useEffect(() => {
    const isMobile = window.innerWidth < 960;
    
    // Only apply logic on mobile and when opening/open
    if (isMobile && isOpen) {
      // Push hash if not present (using pushState to avoid firing hashchange)
      if (window.location.hash !== '#menu') {
        window.history.pushState(null, '', '#menu');
      }

      const handleHashChange = () => {
        // If hash is removed (User pressed Back), close sidebar
        if (window.location.hash !== '#menu') {
          if (onCloseRef.current) onCloseRef.current();
        }
      };

      window.addEventListener('hashchange', handleHashChange);

      return () => {
        window.removeEventListener('hashchange', handleHashChange);
        
        // If closing manually (hash is still #menu), go back to clean URL
        if (window.location.hash === '#menu') {
           window.history.back();
        }
      };
    }
  }, [isOpen]); // Only re-run when isOpen changes, not onClose

  const [user] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('authUser'));
    } catch (e) {
      return null;
    }
  });

  const hasPermission = (moduleName) => {
    return checkPermission(user, moduleName);
  };

  const links = [
    { to: '/', label: 'Dashboard', icon: FiGrid },
  ];
  
  if (user && user.access_profile === 'admin') {
    links.push({ to: '/business-agent', label: 'Business Agent', icon: FiShield });
  }

  if (hasPermission('companies')) {
    links.push({ to: '/companies', label: 'Empresas', icon: FiBriefcase });
  }

  if (hasPermission('sub_users')) {
    links.push({ to: '/sub-users', label: 'Usuários', icon: FiUser });
  }

  const companyItems = [
    { key: 'files', label: 'Arquivos', to: '/files', icon: FiFolder, permission: 'management' },
    { key: 'stations', label: 'Postos de Serviço', to: '/stations', icon: FiMapPin, permission: 'management' },
    { key: 'employees', label: 'Funcionarios', to: '/employees', icon: FiUsers, permission: 'management' },
    { key: 'vehicles', label: 'Veículos', to: '/vehicles', icon: FiTruck, permission: 'management' },
    { key: 'machineries', label: 'Maquinários', to: '/machineries', icon: FiTool, permission: 'management' },
    { key: 'roles', label: 'Cargos', to: '/roles', icon: FiLayers, permission: 'management' },
    { key: 'reasons', label: 'Motivos', to: '/reasons', icon: FiFlag, permission: 'management' },
    { key: 'specific-documents', label: 'Documentos', to: '/specific-documents', icon: FiFileText, permission: 'management' },
    { key: 'shifts', label: 'Jornadas', to: '/journeys', icon: FiClock, permission: 'management' },
    { key: 'benefits', label: 'Benefícios', to: '/benefits', icon: FiGift, permission: 'management' },
    { key: 'customers', label: 'Clientes', to: '/financial/customers', icon: FiUserCheck, permission: 'financial' },
    { key: 'suppliers', label: 'Fornecedores', to: '/financial/suppliers', icon: FiTruck, permission: 'financial' },
    { key: 'accountants', label: 'Contadores', to: '/financial/accountants', icon: FiUser, permission: 'financial' },
    { key: 'holidays', label: 'Feriados e Facultativos', to: '/holidays', icon: FiCalendar, permission: 'management' },
    { key: 'risk', label: 'Aplicação NR-1', to: '/risk', icon: FiActivity, permission: 'management' },
  ].filter(item => hasPermission(item.permission));

  const financialItems = [
    { key: 'overview', label: 'Visão Geral', to: '/financial/overview', icon: FiPieChart, permission: 'financial' },
    { key: 'transactions', label: 'Receitas e Despesas', to: '/financial/transactions', icon: FiActivity, permission: 'financial' },
    { key: 'accounts', label: 'Contas Bancárias', to: '/financial/accounts', icon: FiCreditCard, permission: 'financial' },
    { key: 'categories', label: 'Categorias', to: '/financial/categories', icon: FiTag, permission: 'financial' },
    { key: 'cost-centers', label: 'Centro de Custo', to: '/financial/cost-centers', icon: FiTarget, permission: 'financial' },
  ].filter(item => hasPermission(item.permission));

  const timeKeepingItems = [
    { key: 'overview', label: 'Visão Geral', to: '/time-keeping/overview', icon: FiPieChart, permission: 'time_keeping' },
    { key: 'executed', label: 'Pontos Executados', to: '/time-keeping/executed-points', icon: FiCheckCircle, permission: 'time_keeping' },
    { key: 'adjustments', label: 'Solicitação de Ajustes', to: '/time-keeping/adjustments', icon: FiEdit3, permission: 'time_keeping' },
    { key: 'absence-launch', label: 'Lançamentos', to: '/time-keeping/absences', icon: FiClipboard, permission: 'time_keeping' },
    { key: 'work-schedules', label: 'Escala de Trabalho', to: '/time-keeping/work-schedules', icon: FiCalendar, permission: 'time_keeping' },
    { key: 'timesheet', label: 'Folha de Ponto', to: '/time-keeping/timesheet', icon: FiFileText, permission: 'time_keeping' },
  ].filter(item => hasPermission(item.permission));

  const rondaItems = [
    { key: 'overview', label: 'Visão Geral', to: '/rounds/overview', icon: FiPieChart, permission: 'patrol' },
    { key: 'executed', label: 'Rondas Executadas', to: '/rounds/executed-rounds', icon: FiCheckCircle, permission: 'patrol' },
    { key: 'config', label: 'Configuração', to: '/rounds/config', icon: FiSettings, permission: 'patrol' },
    { key: 'book', label: 'Livro de Ocorrência', to: '/rounds/book', icon: FiBook, permission: 'patrol' },
  ].filter(item => hasPermission(item.permission));

  const inspectionsItems = [
    { key: 'overview', label: 'Visão Geral', to: '/inspections/overview', icon: FiPieChart, permission: 'inspections' },
    { key: 'executed', label: 'Inspeções Executadas', to: '/inspections/executed', icon: FiCheckCircle, permission: 'inspections' },
    { key: 'config', label: 'Configurações', to: '/inspections/config', icon: FiSettings, permission: 'inspections' },
  ].filter(item => hasPermission(item.permission));

  const serviceOrderItems = [
    { key: 'overview', label: 'Visão Geral', to: '/service-orders/overview', icon: FiPieChart, permission: 'service_orders' },
    { key: 'schedule', label: 'Agenda', to: '/service-orders/schedule', icon: FiCalendar, permission: 'service_orders' },
    { key: 'registrations', label: 'Cadastros', to: '/service-orders/registrations', icon: FiFolder, permission: 'service_orders' },
    { key: 'services', label: 'Serviços', to: '/service-orders/services', icon: FiTool, permission: 'service_orders' },
  ].filter(item => hasPermission(item.permission));

  const location = useLocation();
  const navigate = useNavigate();

  const activeCompanyItem = companyItems.find(
    item => item.to && location.pathname.startsWith(item.to),
  );

  const activeCompanyKey = activeCompanyItem ? activeCompanyItem.key : '';
  const isCompanySectionActive = Boolean(activeCompanyKey);
  const [isCompanySectionOpen, setIsCompanySectionOpen] = useState(isCompanySectionActive);

  const activeFinancialItem = financialItems.find(
    item => item.to && location.pathname.startsWith(item.to),
  );

  const activeFinancialKey = activeFinancialItem ? activeFinancialItem.key : '';
  const isFinancialSectionActive = Boolean(activeFinancialKey);
  const [isFinancialSectionOpen, setIsFinancialSectionOpen] = useState(isFinancialSectionActive);

  const activeTimeKeepingItem = timeKeepingItems.find(
    item => item.to && location.pathname.startsWith(item.to),
  );

  const activeTimeKeepingKey = activeTimeKeepingItem ? activeTimeKeepingItem.key : '';
  const isTimeKeepingSectionActive = Boolean(activeTimeKeepingKey);
  const [isTimeKeepingSectionOpen, setIsTimeKeepingSectionOpen] = useState(isTimeKeepingSectionActive);

  const activeRondaItem = rondaItems.find(
    item => item.to && location.pathname.startsWith(item.to),
  );

  const activeRondaKey = activeRondaItem ? activeRondaItem.key : '';
  const isRondaSectionActive = Boolean(activeRondaKey);
  const [isRondaSectionOpen, setIsRondaSectionOpen] = useState(isRondaSectionActive);

  const activeInspectionsItem = inspectionsItems.find(
    item => item.to && location.pathname.startsWith(item.to),
  );

  const activeInspectionsKey = activeInspectionsItem ? activeInspectionsItem.key : '';
  const isInspectionsSectionActive = Boolean(activeInspectionsKey);
  const [isInspectionsSectionOpen, setIsInspectionsSectionOpen] = useState(isInspectionsSectionActive);

  const activeServiceOrderItem = serviceOrderItems.find(
    item => item.to && location.pathname.startsWith(item.to),
  );

  const activeServiceOrderKey = activeServiceOrderItem ? activeServiceOrderItem.key : '';
  const isServiceOrderSectionActive = Boolean(activeServiceOrderKey);
  const [isServiceOrderSectionOpen, setIsServiceOrderSectionOpen] = useState(isServiceOrderSectionActive);

  const toggleCompanySection = () => {
    setIsCompanySectionOpen(prev => !prev);
    if (!isCompanySectionOpen) {
      setIsFinancialSectionOpen(false);
      setIsTimeKeepingSectionOpen(false);
      setIsRondaSectionOpen(false);
      setIsInspectionsSectionOpen(false);
      setIsServiceOrderSectionOpen(false);
    }
  };

  const toggleFinancialSection = () => {
    setIsFinancialSectionOpen(prev => !prev);
    if (!isFinancialSectionOpen) {
      setIsCompanySectionOpen(false);
      setIsTimeKeepingSectionOpen(false);
      setIsRondaSectionOpen(false);
      setIsInspectionsSectionOpen(false);
      setIsServiceOrderSectionOpen(false);
    }
  };

  const toggleTimeKeepingSection = () => {
    setIsTimeKeepingSectionOpen(prev => !prev);
    if (!isTimeKeepingSectionOpen) {
      setIsCompanySectionOpen(false);
      setIsFinancialSectionOpen(false);
      setIsRondaSectionOpen(false);
      setIsInspectionsSectionOpen(false);
      setIsServiceOrderSectionOpen(false);
    }
  };

  const toggleRondaSection = () => {
    setIsRondaSectionOpen(prev => !prev);
    if (!isRondaSectionOpen) {
      setIsCompanySectionOpen(false);
      setIsFinancialSectionOpen(false);
      setIsTimeKeepingSectionOpen(false);
      setIsInspectionsSectionOpen(false);
      setIsServiceOrderSectionOpen(false);
    }
  };

  const toggleInspectionsSection = () => {
    setIsInspectionsSectionOpen(prev => !prev);
    if (!isInspectionsSectionOpen) {
      setIsCompanySectionOpen(false);
      setIsFinancialSectionOpen(false);
      setIsTimeKeepingSectionOpen(false);
      setIsRondaSectionOpen(false);
      setIsServiceOrderSectionOpen(false);
    }
  };

  const toggleServiceOrderSection = () => {
    setIsServiceOrderSectionOpen(prev => !prev);
    if (!isServiceOrderSectionOpen) {
      setIsCompanySectionOpen(false);
      setIsFinancialSectionOpen(false);
      setIsTimeKeepingSectionOpen(false);
      setIsRondaSectionOpen(false);
      setIsInspectionsSectionOpen(false);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  return (
    <>
      {isOpen && <div className="app-sidebar-backdrop" onClick={onClose} />}
      <aside className={`app-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="app-sidebar-inner">
          <div className="app-sidebar-mobile-header">
            <span className="app-sidebar-mobile-title">Menu</span>
            <button className="app-sidebar-close-button" onClick={onClose}>
              <FiX size={24} />
            </button>
          </div>

          <div className="app-sidebar-section">
            <span className="app-sidebar-section-title">Navegação</span>
            <nav className="app-sidebar-nav">
              {links.map(link => {
                const Icon = link.icon;

                return (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    className={({ isActive }) =>
                      [
                        'app-sidebar-link',
                        isActive && 'active',
                      ]
                        .filter(Boolean)
                        .join(' ')
                    }
                    onClick={onClose}
                  >
                    <Icon size={18} />
                    <span>{link.label}</span>
                  </NavLink>
                );
              })}

              {hasPermission('management') && (
                <>
                  <button
                    type="button"
                    className={[
                      'app-sidebar-link',
                      'app-sidebar-link-parent',
                      isCompanySectionActive && 'active',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={toggleCompanySection}
                  >
                    <div className="app-sidebar-link-main">
                      <FiBriefcase size={18} />
                      <span>Gestão</span>
                    </div>
                    <div className={`app-sidebar-chevron ${isCompanySectionOpen ? 'open' : ''}`}>
                      <FiChevronDown size={14} />
                    </div>
                  </button>

                  {isCompanySectionOpen && (
                    <div className="app-sidebar-subnav">
                      {companyItems.map(item => {
                        const ItemIcon = item.icon;
                        const isActive = item.key === activeCompanyKey;

                        return (
                          <button
                            key={item.key}
                            type="button"
                            className={[
                              'app-sidebar-sublink',
                              isActive && 'active',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={
                              item.to
                                ? () => handleNavigation(item.to)
                                : undefined
                            }
                          >
                            <div className="app-sidebar-sublink-main">
                              {ItemIcon && <ItemIcon size={15} />}
                              <span>{item.label}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {hasPermission('financial') && (
                <>
                  <button
                    type="button"
                    className={[
                      'app-sidebar-link',
                      'app-sidebar-link-parent',
                      isFinancialSectionActive && 'active',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={toggleFinancialSection}
                  >
                    <div className="app-sidebar-link-main">
                      <FiDollarSign size={18} />
                      <span>Financeiro</span>
                    </div>
                    <div className={`app-sidebar-chevron ${isFinancialSectionOpen ? 'open' : ''}`}>
                      <FiChevronDown size={14} />
                    </div>
                  </button>

                  {isFinancialSectionOpen && (
                    <div className="app-sidebar-subnav">
                      {financialItems.map(item => {
                        const ItemIcon = item.icon;
                        const isActive = item.key === activeFinancialKey;

                        return (
                          <button
                            key={item.key}
                            type="button"
                            className={[
                              'app-sidebar-sublink',
                              isActive && 'active',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={
                              item.to
                                ? () => handleNavigation(item.to)
                                : undefined
                            }
                          >
                            <div className="app-sidebar-sublink-main">
                              {ItemIcon && <ItemIcon size={15} />}
                              <span>{item.label}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {hasPermission('time_keeping') && (
                <>
                  <button
                    type="button"
                    className={[
                      'app-sidebar-link',
                      'app-sidebar-link-parent',
                      isTimeKeepingSectionActive && 'active',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={toggleTimeKeepingSection}
                  >
                    <div className="app-sidebar-link-main">
                      <FiCalendar size={18} />
                      <span>Controle de Ponto</span>
                    </div>
                    <div className={`app-sidebar-chevron ${isTimeKeepingSectionOpen ? 'open' : ''}`}>
                      <FiChevronDown size={14} />
                    </div>
                  </button>

                  {isTimeKeepingSectionOpen && (
                    <div className="app-sidebar-subnav">
                      {timeKeepingItems.map(item => {
                        const ItemIcon = item.icon;
                        const isActive = item.key === activeTimeKeepingKey;

                        return (
                          <button
                            key={item.key}
                            type="button"
                            className={[
                              'app-sidebar-sublink',
                              isActive && 'active',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={
                              item.to
                                ? () => handleNavigation(item.to)
                                : undefined
                            }
                          >
                            <div className="app-sidebar-sublink-main">
                              {ItemIcon && <ItemIcon size={15} />}
                              <span>{item.label}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {hasPermission('patrol') && (
                <>
                  <button
                    type="button"
                    className={[
                      'app-sidebar-link',
                      'app-sidebar-link-parent',
                      isRondaSectionActive && 'active',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={toggleRondaSection}
                  >
                    <div className="app-sidebar-link-main">
                      <FiShield size={18} />
                      <span>Ronda&nbsp;de&nbsp;Vigilância</span>
                    </div>
                    <div className={`app-sidebar-chevron ${isRondaSectionOpen ? 'open' : ''}`}>
                      <FiChevronDown size={14} />
                    </div>
                  </button>

                  {isRondaSectionOpen && (
                    <div className="app-sidebar-subnav">
                      {rondaItems.map(item => {
                        const ItemIcon = item.icon;
                        const isActive = item.key === activeRondaKey;

                        return (
                          <button
                            key={item.key}
                            type="button"
                            className={[
                              'app-sidebar-sublink',
                              isActive && 'active',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={
                              item.to
                                ? () => handleNavigation(item.to)
                                : undefined
                            }
                          >
                            <div className="app-sidebar-sublink-main">
                              {ItemIcon && <ItemIcon size={15} />}
                              <span>{item.label}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {hasPermission('inspections') && (
                <>
                  <button
                    type="button"
                    className={[
                      'app-sidebar-link',
                      'app-sidebar-link-parent',
                      isInspectionsSectionActive && 'active',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={toggleInspectionsSection}
                  >
                    <div className="app-sidebar-link-main">
                      <FiClipboard size={18} />
                      <span>Inspeções</span>
                    </div>
                    <div className={`app-sidebar-chevron ${isInspectionsSectionOpen ? 'open' : ''}`}>
                      <FiChevronDown size={14} />
                    </div>
                  </button>

                  {isInspectionsSectionOpen && (
                    <div className="app-sidebar-subnav">
                      {inspectionsItems.map(item => {
                        const ItemIcon = item.icon;
                        const isActive = item.key === activeInspectionsKey;

                        return (
                          <button
                            key={item.key}
                            type="button"
                            className={[
                              'app-sidebar-sublink',
                              isActive && 'active',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={
                              item.to
                                ? () => handleNavigation(item.to)
                                : undefined
                            }
                          >
                            <div className="app-sidebar-sublink-main">
                              {ItemIcon && <ItemIcon size={15} />}
                              <span>{item.label}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {hasPermission('service_orders') && (
                <>
                  <button
                    type="button"
                    className={[
                      'app-sidebar-link',
                      'app-sidebar-link-parent',
                      isServiceOrderSectionActive && 'active',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={toggleServiceOrderSection}
                  >
                    <div className="app-sidebar-link-main">
                      <FiTool size={18} />
                      <span>Ordens de Serviço</span>
                    </div>
                    <div className={`app-sidebar-chevron ${isServiceOrderSectionOpen ? 'open' : ''}`}>
                      <FiChevronDown size={14} />
                    </div>
                  </button>

                  {isServiceOrderSectionOpen && (
                    <div className="app-sidebar-subnav">
                      {serviceOrderItems.map(item => {
                        const ItemIcon = item.icon;
                        const isActive = item.key === activeServiceOrderKey;

                        return (
                          <button
                            key={item.key}
                            type="button"
                            className={[
                              'app-sidebar-sublink',
                              isActive && 'active',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={
                              item.to
                                ? () => handleNavigation(item.to)
                                : undefined
                            }
                          >
                            <div className="app-sidebar-sublink-main">
                              {ItemIcon && <ItemIcon size={15} />}
                              <span>{item.label}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
              <br/>
            </nav>
          </div>
        </div>
      </aside>
    </>
  );
}
